import React, { useEffect, useState, useRef } from 'react';
import { ChevronLeft, Bug, ChevronDown, Check } from 'lucide-react';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Chip } from '../components/Chip';
interface DropdownOption {
  value: string;
  label: string;
}
interface CustomDropdownProps {
  label: string;
  placeholder: string;
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}
function CustomDropdown({
  label,
  placeholder,
  options,
  value,
  onChange,
  required
}: CustomDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selectedOption = options.find(o => o.value === value);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return <div className="flex flex-col gap-1.5" ref={ref} data-id="element-756">
      <label className="text-sm font-bold text-slate-700 ml-1" data-id="element-757">{label}</label>
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`
          w-full bg-white border-2 rounded-2xl py-3.5 px-4 text-left
          focus:outline-none focus:ring-0 transition-colors duration-200
          flex items-center justify-between
          ${isOpen ? 'border-emerald-400' : 'border-slate-100 hover:border-slate-200'}
          ${selectedOption ? 'text-slate-800' : 'text-slate-400'}
        `} data-id="element-758">
        <span className={selectedOption ? 'font-medium' : 'font-normal'} data-id="element-759">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown size={18} className={`text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-760" />
      </button>

      {isOpen && <div className="relative z-30" data-id="element-761">
          <div className="absolute top-0 left-0 right-0 bg-white border-2 border-slate-100 rounded-2xl shadow-lg overflow-hidden animate-in fade-in zoom-in-95 duration-150" data-id="element-762">
            {options.map(option => <button key={option.value} type="button" onClick={() => {
          onChange(option.value);
          setIsOpen(false);
        }} className={`
                  w-full text-left px-4 py-3 text-sm font-medium transition-colors
                  flex items-center justify-between
                  ${value === option.value ? 'bg-emerald-50 text-emerald-700 font-bold' : 'text-slate-700 hover:bg-slate-50'}
                `} data-id="element-763">
                {option.label}
                {value === option.value && <Check size={16} className="text-emerald-500" data-id="element-764" />}
              </button>)}
          </div>
        </div>}

      {/* Hidden native input for form required validation */}
      {required && <input type="text" value={value} required className="sr-only" tabIndex={-1} onChange={() => {}} data-id="element-765" />}
    </div>;
}
interface OnboardingStep1Props {
  onBack: () => void;
  onNext: () => void;
}
export function OnboardingStep1({
  onBack,
  onNext
}: OnboardingStep1Props) {
  const [age, setAge] = useState('');
  const [sex, setSex] = useState<'Male' | 'Female' | ''>('');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [goal, setGoal] = useState('');
  const [bestDays, setBestDays] = useState<string[]>([]);
  const [duration, setDuration] = useState('');
  // Cycle tracking state
  const [cycleTrackingEnabled, setCycleTrackingEnabled] = useState(false);
  const [lastPeriodStart, setLastPeriodStart] = useState('');
  const [periodLength, setPeriodLength] = useState('');
  const [cycleLength, setCycleLength] = useState('');
  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showCycleBlockError, setShowCycleBlockError] = useState(false);
  // Debug state
  const [showDebug, setShowDebug] = useState(false);
  const goalOptions: DropdownOption[] = [{
    value: 'hypertrophy',
    label: 'Build Muscle'
  }, {
    value: 'fat_loss',
    label: 'Lose Weight'
  }, {
    value: 'strength',
    label: 'Get Stronger'
  }, {
    value: 'recomposition',
    label: 'Recomposition'
  }];
  const durationOptions: DropdownOption[] = [{
    value: '4_weeks',
    label: '4 Weeks'
  }, {
    value: '8_weeks',
    label: '8 Weeks'
  }, {
    value: '12_weeks',
    label: '12 Weeks'
  }];
  const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const toggleDay = (day: string) => {
    setBestDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };
  const validateLastPeriodStart = (value: string): string => {
    if (!value) return 'Enter the start date of your last period';
    const date = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (isNaN(date.getTime())) return 'Choose a valid date';
    if (date > today) return 'The date cannot be in the future';
    const daysAgo = Math.floor((today.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (daysAgo > 120) return 'Enter a more recent date or skip cycle tracking for now';
    return '';
  };
  const validatePeriodLength = (value: string, cycleLengthValue?: string): string => {
    if (!value) return 'Enter your typical period length';
    const num = Number(value);
    if (!Number.isInteger(num)) return 'Period length must be a whole number';
    if (num < 1 || num > 14) return 'Period length must be between 1 and 14 days';
    const cycleNum = Number(cycleLengthValue || cycleLength);
    if (cycleNum && num > cycleNum) {
      return 'Period length cannot be longer than your cycle length';
    }
    return '';
  };
  const validateCycleLength = (value: string, periodLengthValue?: string): string => {
    if (!value) return 'Enter your typical cycle length';
    const num = Number(value);
    if (!Number.isInteger(num)) return 'Cycle length must be a whole number';
    if (num < 15 || num > 90) return 'Cycle length must be between 15 and 90 days';
    const periodNum = Number(periodLengthValue || periodLength);
    if (periodNum && num < periodNum) {
      return 'Cycle length cannot be shorter than your period length';
    }
    return '';
  };
  const handleBlur = (field: string) => {
    setTouched({
      ...touched,
      [field]: true
    });
    let error = '';
    if (field === 'lastPeriodStart') {
      error = validateLastPeriodStart(lastPeriodStart);
    } else if (field === 'periodLength') {
      error = validatePeriodLength(periodLength);
    } else if (field === 'cycleLength') {
      error = validateCycleLength(cycleLength);
    }
    setErrors({
      ...errors,
      [field]: error
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validate cycle tracking only if female AND tracking is enabled
    if (sex === 'Female' && cycleTrackingEnabled) {
      const filledCount = [lastPeriodStart, periodLength, cycleLength].filter(Boolean).length;
      // All 3 fields are required when cycle tracking is enabled
      if (filledCount < 3) {
        setShowCycleBlockError(true);
        return;
      }
      // Validate all fields
      const newErrors: Record<string, string> = {};
      newErrors.lastPeriodStart = validateLastPeriodStart(lastPeriodStart);
      newErrors.periodLength = validatePeriodLength(periodLength, cycleLength);
      newErrors.cycleLength = validateCycleLength(cycleLength, periodLength);
      setErrors(newErrors);
      setTouched({
        lastPeriodStart: true,
        periodLength: true,
        cycleLength: true
      });
      if (Object.values(newErrors).some(Boolean)) {
        return;
      }
      setShowCycleBlockError(false);
    }
    onNext();
  };
  const loadDebugState = (state: string) => {
    setShowDebug(false);
    setErrors({});
    setTouched({});
    setShowCycleBlockError(false);
    switch (state) {
      case 'female-empty':
        setSex('Female');
        setCycleTrackingEnabled(false);
        setLastPeriodStart('');
        setPeriodLength('');
        setCycleLength('');
        break;
      case 'female-one-field':
        setSex('Female');
        setCycleTrackingEnabled(true);
        setLastPeriodStart('2026-03-01');
        setPeriodLength('');
        setCycleLength('');
        break;
      case 'female-incomplete-error':
        setSex('Female');
        setCycleTrackingEnabled(true);
        setLastPeriodStart('2026-03-01');
        setPeriodLength('5');
        setCycleLength('');
        setShowCycleBlockError(true);
        break;
      case 'female-invalid-date':
        setSex('Female');
        setCycleTrackingEnabled(true);
        setLastPeriodStart('2026-12-01');
        setPeriodLength('5');
        setCycleLength('28');
        setErrors({
          lastPeriodStart: 'The date cannot be in the future'
        });
        setTouched({
          lastPeriodStart: true
        });
        break;
      case 'female-period-greater':
        setSex('Female');
        setCycleTrackingEnabled(true);
        setLastPeriodStart('2026-03-01');
        setPeriodLength('30');
        setCycleLength('28');
        setErrors({
          periodLength: 'Period length cannot be longer than your cycle length'
        });
        setTouched({
          periodLength: true
        });
        break;
      case 'female-valid':
        setSex('Female');
        setCycleTrackingEnabled(true);
        setLastPeriodStart('2026-03-01');
        setPeriodLength('5');
        setCycleLength('28');
        break;
      case 'male':
        setSex('Male');
        setCycleTrackingEnabled(false);
        setLastPeriodStart('');
        setPeriodLength('');
        setCycleLength('');
        break;
    }
  };
  return <div className="flex flex-col h-full w-full bg-stone-50 overflow-hidden relative" data-id="element-766">
      {/* Debug Toggle */}
      <button onClick={() => setShowDebug(!showDebug)} className="absolute top-8 right-2 z-40 w-7 h-7 rounded-full bg-slate-800/70 text-white flex items-center justify-center hover:bg-slate-800 transition-colors" title="Debug panel" data-id="element-767">
        <Bug size={14} data-id="element-768" />
      </button>

      {/* Debug Panel */}
      {showDebug && <div className="absolute top-16 right-2 z-40 bg-slate-800 text-white rounded-2xl p-3 shadow-xl w-64 text-xs animate-in fade-in zoom-in-95 duration-150" data-id="element-769">
          <p className="font-bold text-slate-400 uppercase tracking-wider text-[10px] mb-2" data-id="element-770">
            Debug Panel
          </p>
          <button onClick={() => loadDebugState('female-empty')} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-771">
            ▶ Female — empty cycle section
          </button>
          <button onClick={() => loadDebugState('female-one-field')} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-772">
            ▶ Female — one field filled
          </button>
          <button onClick={() => loadDebugState('female-incomplete-error')} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-773">
            ▶ Female — incomplete + error
          </button>
          <button onClick={() => loadDebugState('female-invalid-date')} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-774">
            ▶ Female — invalid date
          </button>
          <button onClick={() => loadDebugState('female-period-greater')} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-775">
            ▶ Female — period &gt; cycle
          </button>
          <button onClick={() => loadDebugState('female-valid')} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-776">
            ▶ Female — all valid
          </button>
          <button onClick={() => loadDebugState('male')} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium" data-id="element-777">
            ▶ Male — cycle hidden
          </button>
        </div>}

      {/* Header */}
      <div className="flex items-center justify-between p-6 pt-8 bg-stone-50/90 backdrop-blur-md z-20 shrink-0" data-id="element-778">
        <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-slate-200/50 text-slate-600 transition-colors" aria-label="Go back" data-id="element-779">
          <ChevronLeft size={28} data-id="element-780" />
        </button>
        <div className="w-12 h-12 rounded-2xl overflow-hidden border-2 border-white shadow-sm bg-stone-200" data-id="element-781">
          <video autoPlay loop muted playsInline className="w-full h-full object-cover" src="https://cdn.magicpatterns.com/uploads/69mUG6ooMsKbP89Ta37mkt/Katethought_friendly_gym_mascot_character_funny_good_guy_musc_bc3cfd10-111a-4766-8e76-81af2852edc8_2.mp4" data-id="element-782" />
        </div>
        <div className="w-10" data-id="element-783" />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 pb-32" data-id="element-784">
        <div className="mb-8 mt-2" data-id="element-785">
          <span className="text-sm font-extrabold text-emerald-500 tracking-wider uppercase mb-2 block" data-id="element-786">
            Step 1 of 2
          </span>
          <h1 className="text-3xl font-extrabold text-slate-800 mb-2" data-id="element-787">
            Hi there! 👋
          </h1>
          <p className="text-slate-500 font-medium leading-relaxed" data-id="element-788">
            We'd like to learn a bit more about you to build a safer and more
            suitable starting program.
          </p>
        </div>

        <form id="onboarding1-form" onSubmit={handleSubmit} className="space-y-6" data-id="element-789">
          <div className="grid grid-cols-2 gap-4" data-id="element-790">
            <Input label="Age" type="number" placeholder="e.g. 28" value={age} onChange={e => setAge(e.target.value)} required data-id="element-791" />
            <div className="flex flex-col gap-1.5" data-id="element-792">
              <label className="text-sm font-bold text-slate-700 ml-1" data-id="element-793">
                Sex
              </label>
              <div className="flex gap-2 h-[52px] items-center" data-id="element-794">
                <Chip label="Male" selected={sex === 'Male'} onClick={() => {
                setSex('Male');
                setCycleTrackingEnabled(false);
                setLastPeriodStart('');
                setPeriodLength('');
                setCycleLength('');
                setErrors({});
                setTouched({});
                setShowCycleBlockError(false);
              }} className="flex-1" data-id="element-795" />
                <Chip label="Female" selected={sex === 'Female'} onClick={() => setSex('Female')} className="flex-1" data-id="element-796" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-797">
            <Input label="Height (cm)" type="number" placeholder="e.g. 175" value={height} onChange={e => setHeight(e.target.value)} required data-id="element-798" />
            <Input label="Weight (kg)" type="number" placeholder="e.g. 70" value={weight} onChange={e => setWeight(e.target.value)} required data-id="element-799" />
          </div>

          {sex === 'Female' && <div className="animate-in fade-in slide-in-from-top-2" data-id="element-800">
              {!cycleTrackingEnabled ? <button type="button" onClick={() => setCycleTrackingEnabled(true)} className="w-full p-4 bg-pink-50/50 border border-pink-100 rounded-3xl text-left hover:bg-pink-50 transition-colors group" data-id="element-801">
                  <p className="text-sm font-bold text-slate-600 group-hover:text-slate-800 transition-colors" data-id="element-802">
                    Use cycle tracking to better align training load and
                    recovery
                  </p>
                  <p className="text-xs font-medium text-slate-400 mt-1" data-id="element-803">
                    Optional — tap to enable
                  </p>
                </button> : <div className="p-5 bg-pink-50/50 border border-pink-100 rounded-3xl space-y-4 animate-in fade-in slide-in-from-top-2" data-id="element-804">
                  <div className="flex items-center justify-between px-1" data-id="element-805">
                    <p className="text-sm text-slate-500 font-medium leading-relaxed" data-id="element-806">
                      This helps us tailor training and recovery around your
                      cycle.
                    </p>
                    <button type="button" onClick={() => {
                setCycleTrackingEnabled(false);
                setLastPeriodStart('');
                setPeriodLength('');
                setCycleLength('');
                setErrors({});
                setTouched({});
                setShowCycleBlockError(false);
              }} className="text-xs font-bold text-slate-400 hover:text-slate-600 transition-colors shrink-0 ml-3" data-id="element-807">
                      Disable
                    </button>
                  </div>

                  {showCycleBlockError && <div className="px-4 py-3 bg-pink-100/70 border border-pink-200 rounded-2xl animate-in fade-in" data-id="element-808">
                      <p className="text-sm font-medium text-pink-700 leading-relaxed" data-id="element-809">
                        To use cycle tracking, we'll need all three fields
                        filled in. No worries if you'd rather skip this for now.
                      </p>
                    </div>}

                  <Input label="Start of your last period" type="date" value={lastPeriodStart} onChange={e => {
              setLastPeriodStart(e.target.value);
              setShowCycleBlockError(false);
              if (touched.lastPeriodStart) {
                setErrors({
                  ...errors,
                  lastPeriodStart: validateLastPeriodStart(e.target.value)
                });
              }
            }} onBlur={() => handleBlur('lastPeriodStart')} error={touched.lastPeriodStart ? errors.lastPeriodStart : ''} data-id="element-810" />

                  <Input label="Typical period length" type="number" placeholder="days" value={periodLength} onChange={e => {
              setPeriodLength(e.target.value);
              setShowCycleBlockError(false);
              if (touched.periodLength) {
                setErrors({
                  ...errors,
                  periodLength: validatePeriodLength(e.target.value)
                });
              }
            }} onBlur={() => handleBlur('periodLength')} error={touched.periodLength ? errors.periodLength : ''} data-id="element-811" />

                  <Input label="Typical cycle length" type="number" placeholder="days" value={cycleLength} onChange={e => {
              setCycleLength(e.target.value);
              setShowCycleBlockError(false);
              if (touched.cycleLength) {
                setErrors({
                  ...errors,
                  cycleLength: validateCycleLength(e.target.value)
                });
              }
            }} onBlur={() => handleBlur('cycleLength')} error={touched.cycleLength ? errors.cycleLength : ''} data-id="element-812" />
                </div>}
            </div>}

          <CustomDropdown label="Primary goal" placeholder="Select your main goal" options={goalOptions} value={goal} onChange={setGoal} required data-id="element-813" />

          <div className="flex flex-col gap-1.5" data-id="element-814">
            <label className="text-sm font-bold text-slate-700 ml-1" data-id="element-815">
              Which days usually work best for your gym workouts?
            </label>
            <p className="text-xs font-medium text-slate-500 ml-1 mb-1" data-id="element-816">
              Choose the days that usually fit your schedule best.
            </p>
            <div className="flex flex-wrap gap-2" data-id="element-817">
              {weekdays.map(day => <Chip key={day} label={day} selected={bestDays.includes(day)} onClick={() => toggleDay(day)} data-id="element-818" />)}
            </div>
            {bestDays.length > 0 && <p className="text-xs font-bold text-emerald-600 ml-1 mt-1 animate-in fade-in" data-id="element-819">
                {bestDays.length} {bestDays.length === 1 ? 'day' : 'days'} per
                week selected
              </p>}
          </div>

          <CustomDropdown label="Program duration" placeholder="Select duration" options={durationOptions} value={duration} onChange={setDuration} required data-id="element-820" />
        </form>
      </div>

      {/* Sticky Footer */}
      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-stone-50 via-stone-50 to-transparent pt-12 z-20 pointer-events-none" data-id="element-821">
        <div className="pointer-events-auto" data-id="element-822">
          <Button type="submit" form="onboarding1-form" variant="primary" fullWidth data-id="element-823">
            Next
          </Button>
        </div>
      </div>
    </div>;
}